import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { AppSettingsService } from "../app-settings-service/app-settings.service";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import {
    GetCardResponse,
    UpdateCardResponse,
    PatchCardBatchResponse,
    DeleteCardResponse,
    SearchCardsResponse,
} from "../../views-interfaces/card.interface";
import { IBaseCard } from "@app/views-interfaces";
import { CardUpdateRequest } from "@app/views-interfaces/card-update-request";

// Use to build the 'cards' object for bulk change
type CardBatchTypes = Record<string, IBaseCard>;

export class ICardServiceResponse {
    Code: number;
    ContToken: string;
    Cards: IBaseCard[];
}

@Injectable()
export class CardService {
    private readonly http = inject(HttpClient);
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly dashboardsService = inject(DashboardsService);

    constructor() {}

    public getUrl(path = "cards/", dashboardId = this.dashboardsService.getCurrentDashboardId()) {
        return (
            this.appSettingsService.appSettings.CardsService_URL + this.appSettingsService.appSettings.CardsService_Version + path + dashboardId + "/"
        );
    }

    getCardById(cardId: string, dashboardId = this.dashboardsService.getCurrentDashboardId()) {
        return this.http.get<GetCardResponse>(this.getUrl("cards/", dashboardId) + encodeURIComponent(cardId), {
            headers: new HttpHeaders().set("Content-Type", "application/json; charset=utf-8"),
        });
    }

    getCardsFromServer(top = "99999999", select = "", maxPageSize = "99999999", contToken = undefined) {
        let headers = new HttpHeaders().set("Content-Type", "application/json; charset=utf-8");
        if (maxPageSize) {
            headers = headers.set("Prefer", "odata.maxpagesize=" + maxPageSize);
        }
        if (contToken) {
            headers = headers.set("OData", "odata.nextLink=" + contToken);
        }
        const url = this.getUrl();
        return this.http.get<ICardServiceResponse>(url + "?$top=" + top + select, { headers });
    }

    updateCard(cardId: string, cardData: { card: CardUpdateRequest }) {
        const card = Object.fromEntries(Object.entries(cardData.card).map(([key, value]) => [key, value === "null" ? null : value]));
        return this.http.put<UpdateCardResponse>(
            this.getUrl() + cardId,
            { card },
            {
                headers: new HttpHeaders().set("Content-Type", "application/json"),
            },
        );
    }

    patchCardBatch(cardBatch) {
        const cardsToSend: CardBatchTypes = {};
        const cardIds = Object.keys(cardBatch);
        cardIds.forEach((cardId) => {
            const card = cardBatch[cardId];
            const properties = Object.keys(card);
            properties.forEach((prop) => {
                if (card[prop] === "null") {
                    card[prop] = null;
                }
            });
            // Format the object to send to the service
            cardsToSend[cardId.toString()] = card;
        });
        return this.http.patch<PatchCardBatchResponse>(
            this.getUrl(),
            { cards: cardsToSend },
            {
                headers: new HttpHeaders().set("Content-Type", "application/json"),
            },
        );
    }

    deleteCard(cardId: string) {
        return this.http.delete<DeleteCardResponse>(this.getUrl() + cardId);
    }

    searchCardsByTerm(filterString: string) {
        return this.http.get<SearchCardsResponse>(this.getUrl() + "?$filter=" + filterString + "&$top=50");
    }
}
